<script setup lang="ts">
import { ref, watch } from 'vue'
import { User, X, CheckCircle2 } from 'lucide-vue-next'
import { useUserStore, useAccountStore } from '@/stores'
import { API_URL } from '@/constants/api'
import apiHelper from '@/utils/apiHelper'
import { ERROR_TYPE } from '@/constants/consts'
import { $dialog } from '@/plugins/dialog-alert'

import {
  NAME_MAX_LENGTH,
  ORGANIZATION_MAX_LENGTH,
  isValidEmail,
  isValidName,
  isValidOrganization,
  isValidPhoneNo,
  formatPhoneNo
} from '@/utils/validation'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'updated'): void
}>()

const userStore = useUserStore()
const accountStore = useAccountStore()

const userId = ref('')
const userName = ref('')
const organization = ref('')
const phoneNo = ref('')
const reason = ref('')
const roleLabel = ref('')
const userNo = ref<number>(0)

const currentPw = ref('')
const newPw = ref('')
const confirmPw = ref('')

const isSaving = ref(false)
const formError = ref('')
const formSuccess = ref('')

const userNameError = ref('')
const orgError = ref('')
const phoneError = ref('')
const currentPwError = ref('')
const pwError = ref('')
const confirmPwError = ref('')

// 모달 열릴 때 현재 로그인한 유저 정보 로드
watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      resetForm()
      await loadProfileInfo()
    }
  },
  { immediate: true },
)

function resetForm() {
  currentPw.value = ''
  newPw.value = ''
  confirmPw.value = ''
  formError.value = ''
  formSuccess.value = ''
  userNameError.value = ''
  orgError.value = ''
  phoneError.value = ''
  currentPwError.value = ''
  pwError.value = ''
  confirmPwError.value = ''
  isSaving.value = false
}

function handleUserNameInput() {
  const val = userName.value.trim()
  if (val.length > NAME_MAX_LENGTH) {
    userNameError.value = `성명은 ${NAME_MAX_LENGTH}자 이내로 입력해 주세요.`
  } else {
    userNameError.value = ''
  }
}

function handleOrgInput() {
  const val = organization.value.trim()
  if (val.length > ORGANIZATION_MAX_LENGTH) {
    orgError.value = `소속 기관/부서는 ${ORGANIZATION_MAX_LENGTH}자 이내로 입력해 주세요.`
  } else {
    orgError.value = ''
  }
}

function handlePhoneInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  phoneNo.value = formatPhoneNo(val)
  if (phoneNo.value && !isValidPhoneNo(phoneNo.value)) {
    phoneError.value = '올바른 휴대전화 번호 형식이 아닙니다. (예: 010-1234-5678)'
  } else {
    phoneError.value = ''
  }
}

function handleNewPwInput() {
  const val = newPw.value
  if (val.length > 0 && val.length < 4) {
    pwError.value = '비밀번호는 4~30자 이내로 입력해 주세요.'
  } else if (val.length > 30) {
    pwError.value = '비밀번호는 30자 이내로 입력해 주세요.'
  } else {
    pwError.value = ''
  }

  if (confirmPw.value) {
    handleConfirmPwInput()
  }
}

function handleConfirmPwInput() {
  const val = confirmPw.value
  if (!val) {
    confirmPwError.value = ''
  } else if (newPw.value && val !== newPw.value) {
    confirmPwError.value = '비밀번호와 비밀번호 확인이 일치하지 않습니다.'
  } else {
    confirmPwError.value = ''
  }
}

async function loadProfileInfo() {
  // 기본 정보 세팅
  userId.value = accountStore.userId || userStore.currentUser.email || ''
  userName.value = accountStore.userName || userStore.currentUser.name || ''
  roleLabel.value = (userStore.roleAccentColor as any)?.label || userStore.currentUser.role || ''
  userNo.value = accountStore.userNo || 0

  try {
    const res = await apiHelper.get<any>(API_URL.PROFILE.INFO)

    if (res && res.errorCode === 0 && res.data) {
      const myUser: any = res.data

      if (myUser) {
        userNo.value = myUser.userNo ?? userNo.value
        userId.value = myUser.userId || userId.value
        userName.value = myUser.userName || userName.value
        organization.value = myUser.organization || ''
        phoneNo.value = formatPhoneNo(myUser.phoneNo || '')
        reason.value = myUser.reason || ''
      }
    }
  } catch (e) {
    console.error('Failed to load profile details:', e)
  }
}

async function handleSaveProfile() {
  formError.value = ''
  formSuccess.value = ''

  if (!userName.value.trim()) {
    userNameError.value = '성명을 입력해 주세요.'
    return
  }
  if (!isValidName(userName.value)) {
    userNameError.value = `성명은 ${NAME_MAX_LENGTH}자 이내로 입력해 주세요.`
    return
  }

  if (!organization.value.trim()) {
    orgError.value = '소속 기관/부서를 입력해 주세요.'
    return
  }
  if (!isValidOrganization(organization.value)) {
    orgError.value = `소속 기관/부서는 ${ORGANIZATION_MAX_LENGTH}자 이내로 입력해 주세요.`
    return
  }

  if (phoneNo.value && !isValidPhoneNo(phoneNo.value)) {
    phoneError.value = '올바른 휴대전화 번호 형식이 아닙니다. (예: 010-1234-5678)'
    return
  }

  if (newPw.value || confirmPw.value) {
    // 비밀번호를 바꿀 때는 현재 비밀번호를 함께 보내야 한다(서버에서 확인, 불일치 시 40013).
    if (!currentPw.value) {
      currentPwError.value = '현재 비밀번호를 입력해 주세요.'
      return
    }
    if (newPw.value.length < 4) {
      pwError.value = '비밀번호는 4~30자 이내로 입력해 주세요.'
      return
    }
    if (newPw.value.length > 30) {
      pwError.value = '비밀번호는 30자 이내로 입력해 주세요.'
      return
    }
    if (newPw.value !== confirmPw.value) {
      confirmPwError.value = '비밀번호와 비밀번호 확인이 일치하지 않습니다.'
      return
    }
  }

  isSaving.value = true
  try {
    const payload: any = {
      userName: userName.value.trim(),
      organization: organization.value.trim(),
      phoneNo: phoneNo.value.trim(),
      reason: reason.value.trim(),
    }

    if (newPw.value && newPw.value.trim()) {
      payload.userPw = currentPw.value
      payload.userNewPw = newPw.value.trim()
    }

    const res = await apiHelper.put(API_URL.PROFILE.MODIFY, payload)

    if (res?.errorCode === ERROR_TYPE.INVALID_CURRENT_PASSWORD.errorCode) {
      currentPwError.value = ERROR_TYPE.INVALID_CURRENT_PASSWORD.message
    } else if (res && res.errorCode === 0) {
      $dialog.success('수정 완료', '사용자 정보가 성공적으로 수정되었습니다.')

      // 세션 정보 갱신
      if (userName.value) {
        userStore.currentUser.name = userName.value.trim()
        accountStore.userName = userName.value.trim()
      }

      emit('updated')
      emit('close')
    } else {
      formError.value = res?.errorMsg || '정보 수정 중 오류가 발생했습니다.'
    }
  } catch (e: any) {
    if (e?.errorCode === ERROR_TYPE.INVALID_CURRENT_PASSWORD.errorCode) {
      currentPwError.value = ERROR_TYPE.INVALID_CURRENT_PASSWORD.message
    } else {
      formError.value = e?.errorMsg || e?.message || '정보 수정 처리 중 오류가 발생했습니다.'
    }
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[5000] flex items-center justify-center p-4 overflow-y-auto"
    >
      <!-- 배경 오버레이 -->
      <div class="fixed inset-0 bg-slate-900/65 backdrop-blur-sm" />

      <!-- 모달 컨테이너 -->
      <div
        class="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-slate-100 z-10"
      >
        <!-- 헤더 (Dark Navy #0F1A3A) -->
        <div
          class="px-6 py-4 text-white flex items-center justify-between shadow-sm relative overflow-hidden border-b border-slate-800"
          style="background-color: var(--surface-dark, #0f1a3a)"
        >
          <div class="flex items-center gap-2.5">
            <User class="w-5 h-5 text-blue-400 shrink-0" />
            <h3 class="font-bold text-base text-white tracking-tight">내 정보 수정</h3>
          </div>
          <button
            @click="emit('close')"
            class="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- 폼 영역 -->
        <form
          @submit.prevent="handleSaveProfile"
          class="p-6 space-y-4 max-h-[80vh] overflow-y-auto"
        >
          <!-- 1. 아이디 (이메일) & 부여 권한 -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-slate-700">아이디 (이메일)</label>
              <input
                :value="userId"
                disabled
                type="text"
                class="w-full px-3.5 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm font-semibold text-slate-500 outline-none select-none"
              />
            </div>
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-slate-700">현재 권한</label>
              <div
                class="px-3.5 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-2 h-[42px]"
              >
                <span>{{ roleLabel }}</span>
              </div>
            </div>
          </div>

          <!-- 2. 성명 -->
          <div class="space-y-1.5">
            <label class="block text-xs font-bold text-slate-700">
              성명 <span class="text-rose-500">*</span>
            </label>
            <input
              v-model="userName"
              type="text"
              placeholder="성명을 입력하세요"
              :class="[
                'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                userNameError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
              ]"
              @input="handleUserNameInput"
            />
            <p v-if="userNameError" class="text-xs text-rose-500 font-semibold">{{ userNameError }}</p>
          </div>

          <!-- 3. 소속 기관 / 부서 -->
          <div class="space-y-1.5">
            <label class="block text-xs font-bold text-slate-700">
              소속 기관 / 부서 <span class="text-rose-500">*</span>
            </label>
            <input
              v-model="organization"
              type="text"
              placeholder="소속 기관 또는 부서를 입력하세요"
              :class="[
                'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                orgError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
              ]"
              @input="handleOrgInput"
            />
            <p v-if="orgError" class="text-xs text-rose-500 font-semibold">{{ orgError }}</p>
          </div>

          <!-- 4. 연락처 -->
          <div class="space-y-1.5">
            <label class="block text-xs font-bold text-slate-700">
              연락처 <span class="text-slate-400 font-normal">(숫자만 입력)</span>
            </label>
            <input
              v-model="phoneNo"
              type="text"
              placeholder="010-0000-0000"
              :class="[
                'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                phoneError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
              ]"
              @input="handlePhoneInput"
            />
            <p v-if="phoneError" class="text-xs text-rose-500 font-semibold">{{ phoneError }}</p>
          </div>

          <!-- 5. 비밀번호 변경 영역 -->
          <div class="pt-2 border-t border-slate-100 space-y-3">
            <div class="flex items-center gap-1.5 text-xs font-bold text-slate-800">
              <span
                >비밀번호 변경
                <span class="text-slate-400 font-normal">(변경할 경우에만 입력, 4~30자)</span></span
              >
            </div>

            <div class="space-y-1">
              <label class="block text-[11px] font-bold text-slate-600">현재 비밀번호</label>
              <input
                v-model="currentPw"
                type="password"
                autocomplete="current-password"
                placeholder="비밀번호를 변경하려면 현재 비밀번호 입력"
                :class="[
                  'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  currentPwError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                ]"
                @input="currentPwError = ''"
              />
              <p v-if="currentPwError" class="text-xs text-rose-500 font-semibold ml-0.5">{{ currentPwError }}</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="space-y-1">
                <label class="block text-[11px] font-bold text-slate-600">새 비밀번호</label>
                <input
                  v-model="newPw"
                  type="password"
                  autocomplete="new-password"
                  placeholder="새 비밀번호 입력"
                  :class="[
                    'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                    pwError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                  ]"
                  @input="handleNewPwInput"
                />
                <p v-if="pwError" class="text-xs text-rose-500 font-semibold ml-0.5">{{ pwError }}</p>
              </div>

              <div class="space-y-1">
                <label class="block text-[11px] font-bold text-slate-600">새 비밀번호 확인</label>
                <input
                  v-model="confirmPw"
                  type="password"
                  autocomplete="new-password"
                  placeholder="새 비밀번호 확인"
                  :class="[
                    'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                    confirmPwError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                  ]"
                  @input="handleConfirmPwInput"
                />
                <p v-if="confirmPwError" class="text-xs text-rose-500 font-semibold ml-0.5">{{ confirmPwError }}</p>
              </div>
            </div>
          </div>


          <!-- 에러 메시지 -->
          <p v-if="formError" class="text-xs text-rose-500 font-semibold">{{ formError }}</p>

          <!-- 버튼 영역 -->
          <div class="pt-4 flex justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              @click="emit('close')"
              class="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              :disabled="isSaving"
              class="px-5 py-2.5 rounded-xl bg-[#0D1B3E] hover:bg-[#1a2d5a] text-white text-xs font-bold shadow transition-all cursor-pointer active:scale-95 disabled:opacity-50"
            >
              {{ isSaving ? '저장 중...' : '정보 저장' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
